import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation, useHistory } from "react-router-dom";
import styled from "styled-components";
import b from "./img3/logooo.png";

const COLORS = {
  primaryDark: "#081a2f",
  primaryLight: "#edf3ef",
  accent: "#00a8d6",
};

const MenuLabel = styled.button`
  display: none;

  @media (max-width: 991px) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0.7rem;
    right: 1rem;
    width: 3.5rem;
    height: 3.5rem;
    border: none;
    border-radius: 50%;
    background: ${COLORS.primaryDark};
    cursor: pointer;
    z-index: 1001;
    box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  }
`;

const Icon = styled.span`
  position: relative;
  width: 1.8rem;
  height: 2px;
  background: ${(props) =>
    props.clicked ? "transparent" : "white"};
  transition: 0.3s;

  &::before,
  &::after {
    content: "";
    position: absolute;
    right: 0;
    width: 1.8rem;
    height: 2px;
    background: white;
    transition: 0.3s;
  }

  &::before {
    transform: ${(props) =>
      props.clicked ? "rotate(45deg)" : "translateY(-8px)"};
  }

  &::after {
    transform: ${(props) =>
      props.clicked ? "rotate(-45deg)" : "translateY(8px)"};
  }
`;

const NavBackground = styled.div`
  position: fixed;
  top: 1rem;
  right: 1rem;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  z-index: 998;
  pointer-events: none;

  background: radial-gradient(
    ${COLORS.primaryDark},
    #123b5d
  );

  transform: ${(props) =>
    props.clicked ? "scale(60)" : "scale(0)"};

  transition: transform 0.8s cubic-bezier(0.86, 0, 0.07, 1);

  @media (min-width: 992px) {
    display: none;
  }
`;

const Navigation = styled.nav`
  position: fixed;
  inset: 0;
  z-index: 999;
  width: 100%;
  height: 100vh;
  height: 100dvh;

  display: flex;
  align-items: center;
  justify-content: center;

  opacity: ${(props) => (props.clicked ? "1" : "0")};
  visibility: ${(props) =>
    props.clicked ? "visible" : "hidden"};

  pointer-events: ${(props) =>
    props.clicked ? "auto" : "none"};

  transition: opacity 0.35s ease, visibility 0.35s ease;

  @media (min-width: 992px) {
    display: none;
  }
`;

const List = styled.ul`
  list-style: none;
  padding: 20px 0;
  margin: 0;
  width: 100%;
  text-align: center;

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.65rem;

  max-height: 90vh;
  max-height: 90dvh;
  overflow-y: auto;
  overscroll-behavior: contain;
`;

const ItemLink = styled(NavLink)`
  display: inline-block;
  padding: 0.65rem 1.5rem;

  color: ${COLORS.primaryLight};
  font-size: 1.35rem;
  font-weight: 500;
  text-decoration: none;

  border-radius: 8px;
  transition: 0.25s ease;

  &:hover,
  &.active {
    color: white;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const TermsButton = styled.button`
  display: inline-block;
  padding: 0.65rem 1.5rem;

  color: ${COLORS.primaryLight};
  font-size: 1.35rem;
  font-weight: 500;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  transition: 0.25s ease;

  &:hover {
    color: white;
    background: rgba(255, 255, 255, 0.12);
  }
`;

const Logo = styled.img`
  display: block;
  width: 190px;
  max-width: 65vw;
  height: auto;
  margin: 1.5rem auto 0;
  object-fit: contain;
`;

function HamburgerMenu() {
  const [click, setClick] = useState(false);
  const location = useLocation();
  const history = useHistory();

  const closeMenu = useCallback(() => {
    setClick(false);
  }, []);

  const handleClick = () => {
    setClick((prev) => !prev);
  };

  const navigateAndScroll = (path) => {
    closeMenu();

    if (location.pathname === path) {
      window.scrollBy({
        top: 500,
        behavior: "smooth",
      });
      return;
    }

    sessionStorage.setItem("scrollAfterNavigation", "true");
    history.push(path);
  };

  useEffect(() => {
    const shouldScroll = sessionStorage.getItem(
      "scrollAfterNavigation"
    );

    if (shouldScroll === "true") {
      sessionStorage.removeItem("scrollAfterNavigation");

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollBy({
            top: 500,
            behavior: "smooth",
          });
        });
      });
    } else {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    }

    closeMenu();
  }, [location.pathname, closeMenu]);

  useEffect(() => {
    if (click) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [click]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        closeMenu();
      }
    };

    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("keydown", handleEscape);
    };
  }, [closeMenu]);

  const openTerms = () => {
    const fileName =
      "/תקנון-שימוש-באתר - רישוי עסקים ישראל.pdf";

    window.open(
      encodeURI(fileName),
      "_blank",
      "noopener,noreferrer"
    );

    closeMenu();
  };

  return (
    <>
      <MenuLabel
        type="button"
        aria-label={click ? "סגירת תפריט" : "פתיחת תפריט"}
        aria-expanded={click}
        aria-controls="mobile-navigation"
        onClick={handleClick}
      >
        <Icon clicked={click} />
      </MenuLabel>

      <NavBackground clicked={click} />

      <Navigation
        id="mobile-navigation"
        clicked={click}
        aria-hidden={!click}
      >
        <List>
          <li>
            <ItemLink
              to="/"
              end
              onClick={(e) => {
                e.preventDefault();
                navigateAndScroll("/");
              }}
            >
              דף הבית
            </ItemLink>
          </li>

          <li>
            <ItemLink
              to="/customers"
              onClick={(e) => {
                e.preventDefault();
                navigateAndScroll("/customers");
              }}
            >
              מבין לקוחותינו
            </ItemLink>
          </li>

          <li>
            <ItemLink
              to="/about"
              onClick={(e) => {
                e.preventDefault();
                navigateAndScroll("/about");
              }}
            >
              אודות
            </ItemLink>
          </li>

          <li>
            <ItemLink
              to="/contact"
              onClick={(e) => {
                e.preventDefault();
                navigateAndScroll("/contact");
              }}
            >
              יצירת קשר
            </ItemLink>
          </li>

          <li>
            <TermsButton type="button" onClick={openTerms}>
              תקנון האתר
            </TermsButton>
          </li>

          <li>
            <Logo
              src={b}
              alt="רישוי עסקים ישראל"
              loading="lazy"
            />
          </li>
        </List>
      </Navigation>
    </>
  );
}

export default HamburgerMenu;
